import type { CSSProperties } from 'react'
import { RankDetail } from '@/apis/ranking/ranking-api.def.ts'

// Ranking row consumed by the mobile view.
export type RankRow = RankDetail

export { MASKED_NAME } from '@/pages/components/book-idea-link.ts'

// Fully resolved, presentational view model for a single card. The container
// resolves identity strings, self detection, masking and the link target so
// leaf components stay pure and Storybook-mountable from props alone.
export interface RankCardData {
  key: string
  rank: number | null
  rankTotal: number
  identity: string
  isSelf: boolean
  isMasked: boolean
  score?: string
  returnPct: number
  cumulativePnl: number
  dailyPnl: number
  href?: string
  isInactive?: boolean
}

export const inactiveIdentityStyle: CSSProperties = {
  color: 'var(--color-text-disabled)',
}

// Podium border colours by rank (1 gold, 2 silver, 3 bronze). Values are CSS
// variables defined in styles/_colors.scss so the medal frame colour has one
// source of truth.
export const PODIUM_BORDER_COLOR: Record<number, string> = {
  1: 'var(--ranking-medal-gold)',
  2: 'var(--ranking-medal-silver)',
  3: 'var(--ranking-medal-bronze)',
}

// The `*` glyph sits high in its line box; nudge the mask down so it aligns
// on the same baseline as the star and adjacent text.
export const maskedTextStyle: CSSProperties = {
  display: 'inline-block',
  transform: 'translateY(3px)',
}

// The `rank/rankTotal` tag text; a null/empty rank shows a plain dash.
export function rankTagText(rank: number | null, rankTotal: number): string {
  return rank == null ? '-' : `${rank}/${rankTotal}`
}

// PnL sign colour resolved from the card's long/short tokens so the card
// matches the ranking design spec rather than the app-wide number palette.
export function pnlColor(
  value: number,
  colors: { longText?: string; shortText?: string }
): string | undefined {
  if (value > 0) return colors.longText
  if (value < 0) return colors.shortText
  return undefined
}
